import React, { useState, useEffect } from 'react';
import SafeIcon from '../../common/SafeIcon';
import { PRODUCT_CATEGORIES } from '../../data/store';
import { FiPlus, FiX, FiSun, FiMoon, FiCheck } from 'react-icons/fi';
import { apiGetRoutine, apiSaveRoutine, getToken } from '../../services/api';

const defaultRoutines = {
  AM: ['Cleanser', 'Vitamin C Serum', 'SPF 50'],
  PM: ['Oil Cleanser', 'Retinol', 'Moisturizer'],
};

export default function RoutinesView() {
  const [routines, setRoutines] = useState(defaultRoutines);
  const [inputs, setInputs] = useState({ AM: '', PM: '' });
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!getToken()) return;
    apiGetRoutine()
      .then(data => {
        if (data?.AM || data?.PM) {
          setRoutines({ AM: data.AM ?? [], PM: data.PM ?? [] });
        }
      })
      .catch(() => {});
  }, []);

  const addProduct = (time, product) => {
    const trimmed = product.trim();
    if (!trimmed || routines[time].includes(trimmed)) return;
    setRoutines(prev => ({ ...prev, [time]: [...prev[time], trimmed] }));
    setInputs(prev => ({ ...prev, [time]: '' }));
  };

  const removeProduct = (time, product) => {
    setRoutines(prev => ({ ...prev, [time]: prev[time].filter(p => p !== product) }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (getToken()) {
        await apiSaveRoutine(routines);
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      setSaved(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="h-full overflow-y-auto pb-4 px-5 bg-stone-50">
      <div className="pt-6 pb-5 flex items-center gap-3">
        <img
          src="https://media-manager-c.questera.ai/greta-media/1593c15ad88356866a2eabcebc8aef2c67d9fc5afee8d56439ced245a0c6c3417efffbd95aedd1b6f68107af9f17a1fd/images/aW1hZ2UvcG5n/44d2d46ca6ce6a8943aba646038b0408.png"
          alt="Glowlytix Logo"
          className="w-10 h-10 object-contain drop-shadow-sm flex-shrink-0"
        />
        <div>
          <p className="font-sans text-xs text-stone-400 uppercase tracking-widest font-medium mb-0.5">Manage</p>
          <h1 className="font-serif text-3xl text-stone-900 leading-none">My Routines</h1>
        </div>
      </div>

      {['AM', 'PM'].map(time => (
        <div key={time} className="bg-white rounded-3xl p-5 border border-stone-100 shadow-sm mb-4">
          <div className="flex items-center gap-2 mb-4">
            <SafeIcon icon={time === 'AM' ? FiSun : FiMoon} className={`text-xl ${time === 'AM' ? 'text-amber-400' : 'text-indigo-400'}`} />
            <h2 className="font-sans font-semibold text-stone-800 text-base">{time} Routine</h2>
            <span className="ml-auto text-xs font-sans text-stone-400">{routines[time].length} products</span>
          </div>
          <div className="space-y-2 mb-4">
            {routines[time].length === 0 && (
              <p className="text-xs text-stone-300 font-sans text-center py-2">No products added yet</p>
            )}
            {routines[time].map((p, i) => (
              <div key={i} className="flex items-center justify-between bg-stone-50 rounded-2xl px-4 py-3 border border-stone-100">
                <span className="font-sans text-sm text-stone-700">{p}</span>
                <button onClick={() => removeProduct(time, p)} className="text-stone-300 active:text-stone-500 p-0.5">
                  <SafeIcon icon={FiX} className="text-base" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {PRODUCT_CATEGORIES.filter(c => !routines[time].includes(c)).map(cat => (
              <button key={cat} onClick={() => addProduct(time, cat)}
                className="text-xs px-3 py-1.5 rounded-full bg-stone-50 text-stone-500 border border-stone-200 font-sans active:bg-stone-100 transition-colors">
                + {cat}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              value={inputs[time]}
              onChange={e => setInputs(prev => ({ ...prev, [time]: e.target.value }))}
              onKeyDown={e => { if (e.key === 'Enter') addProduct(time, inputs[time]); }}
              placeholder="Add custom product…"
              className="flex-1 text-sm font-sans bg-stone-50 rounded-full px-4 py-2.5 focus:outline-none text-stone-700 placeholder:text-stone-300 border border-stone-100"
            />
            <button onClick={() => addProduct(time, inputs[time])}
              className="w-10 h-10 rounded-full bg-stone-900 flex items-center justify-center text-white flex-shrink-0 active:bg-stone-700 transition-colors">
              <SafeIcon icon={FiPlus} className="text-sm" />
            </button>
          </div>
        </div>
      ))}

      <button onClick={handleSave} disabled={saving}
        className="w-full py-4 rounded-full font-sans font-semibold text-white text-base flex items-center justify-center gap-2 shadow-md active:opacity-90 transition-opacity disabled:opacity-60"
        style={{ background: saved ? '#5C8A78' : 'linear-gradient(135deg, #C07A52 0%, #E8C97A 100%)' }}>
        <SafeIcon icon={saved ? FiCheck : FiPlus} /> {saved ? 'Routine Saved! ✓' : saving ? 'Saving…' : 'Save Routine'}
      </button>
    </div>
  );
}
